import { useMemo } from 'react'

type Variant = 'wave' | 'pulse' | 'glow' | 'dots'

interface Props {
  /** Text repeated across the angled background. Pass the screen's
   *  identity (e.g. "WEEKLY", "I'D EAT THAT"). Rendered uppercase.*/
  text: string
  /** Hex / CSS colour applied to the text + glow. Hub/Weekly/Profile
   *  use the brand lime; game screens use their game accent. */
  color?: string
  /** Force a specific effect. Omit to pick one at random per mount. */
  variant?: Variant
}

const VARIANTS: Variant[] = ['wave', 'pulse', 'glow', 'dots']

/**
 * Decorative background title. Repeats `text` across several angled
 * rows that scroll horizontally at staggered speeds, with a randomly
 * chosen visual effect on each mount so the same screen feels alive
 * across visits without ever distracting from the actual content.
 *
 * Layout rules:
 *   - Absolute fill, contained inside `.screen` (which is the nearest
 *     `relative` ancestor — see index.css).
 *   - `pointer-events: none` so clicks pass straight through to cards.
 *   - Low opacity + clipped to the screen so it never occludes UI.
 *   - aria-hidden because it duplicates the screen's visible heading.
 *
 * Effects compose with the per-row scroll: scroll runs as a transform
 * animation on a child track, so variant animations target unrelated
 * properties (opacity, letter-spacing, filter, text-shadow) and don't
 * fight the scroll.
 */
export function BackgroundTitle({ text, color = '#D9E026', variant }: Props) {
  // Random per mount, stable for the lifetime of this instance.
  const chosen = useMemo<Variant>(
    () => variant ?? VARIANTS[Math.floor(Math.random() * VARIANTS.length)],
    [variant],
  )

  // Repeat with a separator so the scrolling band reads as a wordmark
  // ribbon rather than a paragraph. The repeated string fills 50% of
  // the track twice, which is what the keyframes assume for seamless
  // looping.
  const block = ` ${text.toUpperCase()} · `
  const ribbon = block.repeat(8)

  // Five rows at slightly different vertical positions and speeds.
  // Even rows scroll left, odd rows scroll right, so the screen has
  // gentle counter-motion instead of a uniform drift.
  const rows = [0, 1, 2, 3, 4]

  // Visible scroll speed = track_width / duration. Track width scales
  // with text length, so a fixed duration would make a longer string
  // (e.g. "WEEKLY I'D EAT THAT") visually scroll way faster than a
  // shorter one ("GAMES"). Anchor on the GAMES screen — at 5 chars
  // and ~42s the speed feels right — then scale duration by char
  // count so the perceived speed stays roughly constant on every
  // screen, regardless of how long the page name is.
  const SECONDS_PER_CHAR = 42 / 5
  const baseSeconds = Math.max(20, text.length * SECONDS_PER_CHAR)

  return (
    <div className="bg-title-wrap" aria-hidden="true">
      {rows.map(i => (
        <div
          key={i}
          className="bg-title-row"
          style={{
            top: `${4 + i * 20}%`,
            color,
          }}
        >
          <div
            className={`bg-title-track bg-title-${chosen}`}
            style={{
              animationDuration: `${baseSeconds + i * 6}s`,
              animationDirection: i % 2 ? 'reverse' : 'normal',
            }}
          >
            {/* Two copies side-by-side so the -50% translateX wraps
                seamlessly — when the first copy slides off the left,
                the second is already in view. */}
            <span>{ribbon}</span>
            <span>{ribbon}</span>
          </div>
        </div>
      ))}
    </div>
  )
}
